import { registerIntegration } from "@p0security/shared/integrations/registry";
import {
  type AnyFilterSchema,
  Registration,
} from "@p0security/shared/integrations/types";

import { DatabricksAiAccesses } from "./accesses.ts";
import { DatabricksAiComponents } from "./components.ts";
import { DATABRICKS_AI_INTEGRATION_KEY } from "./constants.ts";
import { DatabricksAiIconUrl } from "./icon.ts";

// The Databricks integration's filters, without `adminRole`, since agents get
// no admin roles. `privilege` holds the Unity Catalog privileges the request's
// level grants, so a rule on writes can match MODIFY or WRITE_VOLUME.
const DatabricksAiFilters = {
  account: { keys: ["id"] },
  catalog: { keys: ["name"] },
  privilege: { keys: ["name"] },
  schema: { keys: ["name"] },
  // The securable the level is granted on: a catalog, schema, table or
  // volume, by dotted name and type.
  securable: { keys: ["name", "type"] },
  // A workspace admin can rename a workspace, so rules should match its ID.
  workspace: { keys: ["id", "name"] },
} as const satisfies AnyFilterSchema;
export type DatabricksAiFilters = typeof DatabricksAiFilters;

// Keyed by component, so a new component doesn't compile until it has one.
// The installer's "add an item" step asks for "the <identifier> of the
// <component> to install".
const IDENTIFIERS: Record<keyof typeof DatabricksAiComponents, string> = {
  account: "ID",
  connector: "AWS account",
};
// `Registration` passes `identifier` a string that may name no component.
const identifiers: Partial<Record<string, string>> = IDENTIFIERS;

export const DatabricksAiRegistration = Registration({
  accesses: DatabricksAiAccesses,
  category: "resource",
  components: DatabricksAiComponents,
  docs: {},
  filters: DatabricksAiFilters,
  identifier: (component) => identifiers[component] ?? component,
  // Each component is named for what one of its items is.
  itemKind: (component) => component,
  label: "Databricks (AI)",
  logoUrl: DatabricksAiIconUrl,
  // No feature flag: the install is visible to every tenant once it merges,
  // with a Beta badge on each component.
  maturity: "beta",
  // The agent's request routes by the CLI's resource-type positional, so
  // `p0 request databricks-ai data …` only works if this is requestable.
  requestType: "requestable",
  // Every request acts in a Databricks account, so the integration is usable
  // once one is installed.
  useMapping: [{ component: "account", use: "iam_write" }],
});

registerIntegration(DATABRICKS_AI_INTEGRATION_KEY, DatabricksAiRegistration);
